// ─── Export Builder Types ───────────────────────────────────────────

/** One row from the export_builder_jobs view */
export interface ExportJobRow {
  // taxicaller_data
  tc_id: string;
  job_id: string;
  project: string | null;
  account_id: string | null;
  reference: string | null;
  passenger_first_name: string | null;
  passenger_last_name: string | null;
  passenger_name: string | null;
  pick_up_location: string | null;
  drop_off_location: string | null;
  pu_date: string | null;
  pu_time: string | null;
  do_time: string | null;
  tc_vehicle_number: string | null;
  tc_driver_name: string | null;
  grand_total: number | null;
  direct_settled: number | null;
  tc_cost_code: string | null;
  stage: string | null;
  fleet: string | null;
  trip_total: number | null;
  trip_base_fare: number | null;
  job_km: number | null;

  // driver_completed_payments
  dcp_id: string | null;
  paid_amount: number | null;
  cash_amt: number | null;
  eftpos_amt: number | null;
  totalmobility_amt: number | null;
  tm_hoist_amt: number | null;
  account_amt: number | null;
  other_amt: number | null;
  discount: number | null;
  eftpos_valid: boolean | null;
  total_mobility_valid: boolean | null;
  tm_hoist_valid: boolean | null;
  reconciled: boolean | null;
  payment_notes: string | null;
  ecard_amt: number | null;
  ecard_valid: boolean | null;
  driver_paid: boolean | null;

  // crm_accounts
  account_name: string | null;
  account_type: string | null;
  account_series_type: string | null;
  billed_status: string | null;
  billing_name: string | null;
  invoice_email: string | null;
  accounts_payable_email: string | null;
  payment_terms: string | null;
  account_category: string | null;
  crm_default_project: string | null;
  crm_default_cost_code: string | null;
  crm_default_reference: string | null;
  crm_is_active: boolean | null;

  // Derived
  account_series: string | null;
  net_after_tm: number | null;
  missing_crm_account: boolean;
  missing_payment_row: boolean;
  missing_project: boolean;
  missing_reference: boolean;
  missing_cost_code: boolean;
}

/** Filter state for the Export Builder */
export interface ExportBuilderFilters {
  dateFrom: string;
  dateTo: string;
  accountSeries: string[];
  accountNumber: string;
  accountName: string;
  billedFilter: 'all' | 'billed' | 'non-billed';
  validatedOnly: boolean;
  stage: string;
  project: string;
  fleet: string;
  driver: string;
  vehicle: string;
}

/** Validation status derived in the frontend */
export type ValidationStatus = 'fully_validated' | 'partially_validated' | 'not_validated' | 'no_payment';

/** Column definition for the grid */
export interface ExportColumnDef {
  key: string;
  label: string;
  group: 'taxicaller' | 'payment' | 'crm' | 'derived';
  width?: number;
  align?: 'left' | 'center' | 'right';
  sortable?: boolean;
  defaultVisible?: boolean;
  render?: (row: ExportJobRow) => React.ReactNode;
}

/** Summary stats computed from the current result set */
export interface ExportSummaryStats {
  rowCount: number;
  totalGrandTotal: number;
  totalTM: number;
  totalHoist: number;
  totalNetAfterTM: number;
  totalAccountAmt: number;
  totalCash: number;
  totalEftpos: number;
  fullyValidated: number;
  partiallyValidated: number;
  notValidated: number;
  noPayment: number;
}


// ─── Template Types ─────────────────────────────────────────────────


/** Source type for a template column */
export type TemplateColumnSourceType =
  | 'source_field'
  | 'static_value'
  | 'blank'
  | 'manual_default'
  | 'calculated'
  | 'date';


/** Supported transform types */
export type TransformType = 'none' | 'clean_address' | 'date_format' | 'trim' | 'town_format' | 'split_select';



/** Calculation type for calculated columns */
export type CalcType = 'concat' | 'math' | 'conditional_text' | 'split' | 'gst' | 'excel_formula';


/** Math operators */
export type MathOperator = 'add' | 'subtract' | 'multiply' | 'divide';

/** Conditional operators */
export type ConditionalOperator = 'eq' | 'neq' | 'gt' | 'gte' | 'lt' | 'lte' | 'is_blank' | 'is_not_blank' | 'is_true' | 'is_false';

// ─── Calculated Column Config Types ─────────────────────────────────

/** A single part in a concat expression */
export interface ConcatPart {
  partType: 'field' | 'text' | 'separator';
  /** For field parts: the source field name */
  field?: string;
  /** For text/separator parts: the literal value */
  value?: string;
  /** Optional transform to apply to field parts */
  transform?: TransformType;
  /** Optional transform config (e.g. date format) */
  transformConfig?: Record<string, any>;
}

/** A single input in a math expression (alternating value/operator) */
export interface MathInput {
  inputType?: 'field' | 'static' | 'margin';
  field?: string;
  value?: number;
  operator?: MathOperator;
  /** For margin input type: the ID of the margin rule to use as a factor */
  marginRuleId?: string;
}


/** Condition for conditional_text */
export interface ConditionalCondition {
  field: string;
  operator: ConditionalOperator;
  value?: any;
}

/** GST output mode */
export type GstOutputMode = 'inclusive' | 'exclusive' | 'gst_only';

/** GST input mode - whether the source amount already includes GST */
export type GstInputMode = 'inclusive' | 'exclusive';

/** Excel formula result type */
export type ExcelFormulaResultType = 'number' | 'string';

/** Full calculated column config stored in transform_config */
export interface CalculatedConfig {
  calcType: CalcType;
  /** For concat */
  parts?: ConcatPart[];
  /** For math */
  inputs?: MathInput[];
  /** For conditional_text */
  condition?: ConditionalCondition;
  thenValue?: string;
  elseValue?: string;
  /** For split */
  splitField?: string;
  splitDelimiter?: string;
  splitPartIndex?: number;
  splitTrimResult?: boolean;
  /** For gst */
  gstSourceField?: string;
  gstRate?: number;
  gstOutputMode?: GstOutputMode;
  gstInputMode?: GstInputMode;
  gstDecimalPlaces?: number;
  /** For excel_formula */
  excelFormulaTemplate?: string;
  excelFormulaResultType?: ExcelFormulaResultType;
}


/** Common separator presets for concat builder */
export const SEPARATOR_PRESETS = [
  { value: ' ', label: 'Space' },
  { value: ' - ', label: 'Hyphen ( - )' },
  { value: ', ', label: 'Comma + Space' },
  { value: ' / ', label: 'Slash ( / )' },
  { value: ': ', label: 'Colon ( : )' },
  { value: ' | ', label: 'Pipe ( | )' },
  { value: ' to ', label: '"to"' },
] as const;

/** Common delimiter presets for split builder */
export const SPLIT_DELIMITER_PRESETS = [
  { value: ' / ', label: 'Slash with spaces ( / )' },
  { value: '/ ', label: 'Slash + space (/ )' },
  { value: '/', label: 'Slash only (/)' },
  { value: ' ', label: 'Space' },
  { value: ', ', label: 'Comma + Space' },
  { value: ' - ', label: 'Hyphen with spaces ( - )' },
  { value: '-', label: 'Hyphen only (-)' },
  { value: ' | ', label: 'Pipe ( | )' },
  { value: ': ', label: 'Colon + Space' },
] as const;



/** Math operator display info */
export const MATH_OPERATOR_INFO: Record<MathOperator, { label: string; symbol: string }> = {
  add: { label: 'Add', symbol: '+' },
  subtract: { label: 'Subtract', symbol: '−' },
  multiply: { label: 'Multiply', symbol: '×' },
  divide: { label: 'Divide', symbol: '÷' },
};

/** Conditional operator display info */
export const CONDITIONAL_OPERATOR_INFO: Record<ConditionalOperator, { label: string; needsValue: boolean }> = {
  eq: { label: 'equals', needsValue: true },
  neq: { label: 'not equals', needsValue: true },
  gt: { label: '> (greater than)', needsValue: true },
  gte: { label: '>= (greater or equal)', needsValue: true },
  lt: { label: '< (less than)', needsValue: true },
  lte: { label: '<= (less or equal)', needsValue: true },
  is_blank: { label: 'is blank', needsValue: false },
  is_not_blank: { label: 'is not blank', needsValue: false },
  is_true: { label: 'is true', needsValue: false },
  is_false: { label: 'is false', needsValue: false },
};

/** Calc type display info */
export const CALC_TYPE_INFO: Record<CalcType, { label: string; description: string; icon: string; color: string }> = {
  concat: {
    label: 'Concatenation',
    description: 'Build a value from multiple fields, text, and separators',
    icon: 'Link',
    color: 'bg-violet-900/40 text-violet-400 border-violet-700/50',
  },
  math: {
    label: 'Math',
    description: 'Simple numeric calculation from fields and values',
    icon: 'Calculator',
    color: 'bg-amber-900/40 text-amber-400 border-amber-700/50',
  },
  conditional_text: {
    label: 'Conditional Text',
    description: 'Return different text based on a field condition',
    icon: 'GitBranch',
    color: 'bg-rose-900/40 text-rose-400 border-rose-700/50',
  },
  split: {
    label: 'Split',
    description: 'Split a field value by a delimiter and keep a specific part',
    icon: 'Scissors',
    color: 'bg-sky-900/40 text-sky-400 border-sky-700/50',
  },
  gst: {
    label: 'GST',
    description: 'Calculate GST-inclusive, GST-exclusive, or GST amount from a price field',
    icon: 'Receipt',
    color: 'bg-emerald-900/40 text-emerald-400 border-emerald-700/50',
  },
  excel_formula: {
    label: 'Excel Formula',
    description: 'Write a live Excel formula for XLSX exports using {{Column Name}} placeholders',
    icon: 'FileSpreadsheet',
    color: 'bg-cyan-900/40 text-cyan-400 border-cyan-700/50',
  },
};

/** GST output mode display info */
export const GST_OUTPUT_MODE_INFO: Record<GstOutputMode, { label: string; description: string; formula: string }> = {
  inclusive: {
    label: 'Price Incl. GST',
    description: 'The total price including GST',
    formula: 'excl × (1 + rate)',
  },
  exclusive: {
    label: 'Price Excl. GST',
    description: 'The price with GST removed',
    formula: 'incl ÷ (1 + rate)',
  },
  gst_only: {
    label: 'GST Amount Only',
    description: 'Just the GST component',
    formula: 'incl − excl',
  },
};

/** GST input mode display info */
export const GST_INPUT_MODE_INFO: Record<GstInputMode, { label: string; description: string }> = {
  inclusive: {
    label: 'GST-Inclusive',
    description: 'The source amount already includes GST (e.g. Grand Total from meter)',
  },
  exclusive: {
    label: 'GST-Exclusive',
    description: 'The source amount does NOT include GST yet',
  },
};

// ─── Blank Column Config Types ──────────────────────────────────────

/** Blank column mode */
export type BlankColumnMode = 'empty' | 'invoice_number';

/** Blank column config stored in transform_config when source_type is 'blank' */
export interface BlankColumnConfig {
  blankMode: BlankColumnMode;
  /** Starting invoice number (only used when blankMode is 'invoice_number') */
  startingInvoiceNumber?: number;
}

/** Blank column mode display info */
export const BLANK_MODE_INFO: Record<BlankColumnMode, { label: string; description: string }> = {
  empty: {
    label: 'Empty',
    description: 'Intentionally blank — no value output',
  },
  invoice_number: {
    label: 'Invoice Number',
    description: 'Auto-incrementing invoice number following invoice run grouping rules',
  },
};



/** Transform type display info */
export const TRANSFORM_TYPE_INFO: Record<TransformType, { label: string; description: string; color: string; needsConfig: boolean }> = {
  none: {
    label: 'None',
    description: 'Use raw value as-is',
    color: 'bg-gray-800/40 text-gray-400 border-gray-700/50',
    needsConfig: false,
  },
  clean_address: {
    label: 'Clean Address',
    description: 'Remove parentheses, trim after comma, shorten suffixes, apply alias rules',
    color: 'bg-cyan-900/40 text-cyan-400 border-cyan-700/50',
    needsConfig: false,
  },
  date_format: {
    label: 'Date Format',
    description: 'Format a date field using a chosen display format',
    color: 'bg-indigo-900/40 text-indigo-400 border-indigo-700/50',
    needsConfig: true,
  },
  trim: {
    label: 'Trim',
    description: 'Trim leading/trailing whitespace',
    color: 'bg-teal-900/40 text-teal-400 border-teal-700/50',
    needsConfig: false,
  },
  town_format: {
    label: 'Town Format',
    description: 'Extract and display the town/city from an address, hiding suburbs based on classification',
    color: 'bg-emerald-900/40 text-emerald-400 border-emerald-700/50',
    needsConfig: true,
  },
  split_select: {
    label: 'Split & Select',
    description: 'Split a value by a delimiter (comma, slash, space, etc.) and choose which parts to keep',
    color: 'bg-sky-900/40 text-sky-400 border-sky-700/50',
    needsConfig: true,
  },
};

/** Split & Select join separator presets */
export const SPLIT_SELECT_JOIN_PRESETS = [
  { value: '/', label: 'Slash (/)' },
  { value: ' / ', label: 'Slash with spaces ( / )' },
  { value: ', ', label: 'Comma + Space' },
  { value: ' ', label: 'Space' },
  { value: ' - ', label: 'Hyphen ( - )' },
  { value: ' | ', label: 'Pipe ( | )' },
] as const;

/** Split & Select config stored in transform_config */
export interface SplitSelectConfig {
  /** The delimiter to split the value by */
  splitSelectDelimiter: string;
  /** 0-indexed array of which parts to include in the output */
  splitSelectParts: number[];
  /** Whether to include ALL parts (overrides splitSelectParts) */
  splitSelectAll: boolean;
  /** The separator to join selected parts back together (default "/") */
  splitSelectJoinWith: string;
  /** Whether to trim each individual part (default true) */
  splitSelectTrimParts: boolean;
}


/** Town format display mode options */
export type TownDisplayMode = 'town_only' | 'suburb_and_city' | 'smart';

/** Town format display mode info */
export const TOWN_DISPLAY_MODE_INFO: Record<TownDisplayMode, { label: string; description: string; example: string }> = {
  town_only: {
    label: 'Town Only',
    description: 'Show only the town/city name (e.g. Feilding, Marton, Palmerston North)',
    example: 'Palmerston North',
  },
  suburb_and_city: {
    label: 'Suburb + City',
    description: 'Show suburb and city when both exist (e.g. Takaro, Palmerston North)',
    example: 'Takaro, Palmerston North',
  },
  smart: {
    label: 'Smart (Recommended)',
    description: 'Show the town if it IS a town. If it is a suburb, show the parent town instead.',
    example: 'Feilding / Palmerston North (not Takaro)',
  },
};

/** Town classification record from the database */
export interface TownClassification {
  id: string;
  location_name: string;
  location_type: 'town' | 'suburb' | 'unknown';
  display_as: string | null;
  parent_town: string | null;
  is_verified: boolean;
  show_in_export: boolean;
  mapbox_place: string | null;
  mapbox_locality: string | null;
  mapbox_neighborhood: string | null;
  mapbox_district: string | null;
  mapbox_region: string | null;
  mapbox_context: any | null;
  source_addresses: string[] | null;
  created_at: string;
  updated_at: string;
}


/** Date format options for the date_format transform */
export const DATE_FORMAT_OPTIONS = [
  { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY', example: '30/03/2026' },
  { value: 'D/M/YYYY', label: 'D/M/YYYY', example: '30/3/2026' },
  { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD', example: '2026-03-30' },
  { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY', example: '03/30/2026' },
  { value: 'DD-MM-YYYY', label: 'DD-MM-YYYY', example: '30-03-2026' },
  { value: 'D MMM YYYY', label: 'D MMM YYYY', example: '30 Mar 2026' },
] as const;

/** An export template record from the database */
export interface ExportTemplate {
  id: string;
  template_name: string;
  description: string | null;
  is_active: boolean;
  target_system: string;
  default_file_type: string;
  created_at: string;
  updated_at: string;
  created_by: string | null;
  updated_by: string | null;
  // Part 9: Official / Locked / Defaults
  is_official: boolean;
  is_locked: boolean;
  default_export_scope: string | null;
  default_include_headers: boolean | null;
  default_sheet_name: string | null;
  default_invoice_grouping_mode: string | null;
  default_invoice_grouping_config: any | null;
  default_starting_invoice_number: number | null;
  default_notes_template: string | null;
  /** Account scope — selected account IDs for this template */
  account_scope: AccountScope | null;
  /** Non-exported account filter — filters rows without adding columns to the export */
  account_filter: AccountFilter | null;
  /** Persisted sort configuration for the output preview: array of {colId, direction} */
  default_sort: SortSpec[] | null;
}

// ─── Sort Spec Type (shared between OutputPreview and template persistence) ──

/** A single sort specification — column ID + direction */
export interface SortSpec {
  colId: string;
  direction: 'asc' | 'desc';
}



// ─── Account Scope Types ────────────────────────────────────────────

/** Account scope mode for templates */
export type AccountScopeMode = 'all' | 'selected';

/** Account scope configuration stored on templates */
export interface AccountScope {
  mode: AccountScopeMode;
  /** Selected account IDs when mode is 'selected' */
  selectedAccountIds: string[];
}

// ─── Non-Exported Account Filter Types ──────────────────────────────

/** Non-exported account filter — saved to template, filters rows without adding columns */
export interface AccountFilter {
  /** Selected account IDs to filter by */
  accountIds: string[];
  /** Which series tabs were active when the filter was configured */
  seriesFilter: string[];
}


/** A CRM account summary for the picker */
export interface AccountPickerItem {
  account_id: string;
  account_name: string | null;
  account_series: string | null;
  billed_status: string | null;
  is_active: boolean | null;
  job_count?: number;
  /** Source of this account record: 'crm', 'jobs', or 'manual' */
  source?: 'crm' | 'jobs' | 'manual';
}




/** An export template column record from the database */
export interface ExportTemplateColumn {
  id: string;
  template_id: string;
  column_order: number;
  export_column_name: string;
  source_type: TemplateColumnSourceType;
  source_field: string | null;
  static_value: string | null;
  default_value: string | null;
  is_required: boolean;
  is_enabled: boolean;
  notes: string | null;
  transform_type: string | null;
  transform_config: any | null;
  created_at: string;
  updated_at: string;
}

/** For creating/updating a template column (without id/timestamps) */
export interface ExportTemplateColumnInput {
  id?: string;
  template_id: string;
  column_order: number;
  export_column_name: string;
  source_type: TemplateColumnSourceType;
  source_field: string | null;
  static_value: string | null;
  default_value: string | null;
  is_required: boolean;
  is_enabled: boolean;
  notes: string | null;
  transform_type?: string | null;
  transform_config?: any | null;
}

// ─── Address Alias Rule Types ───────────────────────────────────────

export type AliasMatchType = 'exact' | 'starts_with' | 'contains' | 'regex';
export type AliasAppliesTo = 'pickup' | 'dropoff' | 'both';

export interface AddressAliasRule {
  id: string;
  rule_name: string;
  match_type: AliasMatchType;
  raw_pattern: string;
  replacement_value: string;
  applies_to: AliasAppliesTo;
  is_active: boolean;
  notes: string | null;
  created_at: string;
  updated_at: string;
  created_by: string | null;
  updated_by: string | null;
}

/** Target system options */
export const TARGET_SYSTEMS = ['MYOB', 'ACC', 'Generic', 'Xero', 'Custom'] as const;

/** File type options */
export const FILE_TYPES = ['csv', 'xlsx'] as const;

/** Source type display info */
export const SOURCE_TYPE_INFO: Record<TemplateColumnSourceType, { label: string; description: string; color: string }> = {
  source_field: {
    label: 'Source Field',
    description: 'Maps directly from joined DMS data',
    color: 'bg-blue-900/40 text-blue-400 border-blue-700/50',
  },
  static_value: {
    label: 'Static Value',
    description: 'Hardcoded value applied to every row',
    color: 'bg-green-900/40 text-green-400 border-green-700/50',
  },
  blank: {
    label: 'Blank',
    description: 'Intentionally blank export column',
    color: 'bg-gray-800/40 text-gray-400 border-gray-700/50',
  },
  manual_default: {
    label: 'Manual Default',
    description: 'Admin-entered default value, editable later',
    color: 'bg-purple-900/40 text-purple-400 border-purple-700/50',
  },
  calculated: {
    label: 'Calculated',
    description: 'Built from concatenation, math, or conditional logic',
    color: 'bg-amber-900/40 text-amber-400 border-amber-700/50',
  },
  date: {
    label: 'Date',
    description: 'Insert a date value — defaults to today\'s date at export time',
    color: 'bg-indigo-900/40 text-indigo-400 border-indigo-700/50',
  },
};

// ─── Date Column Config Types ───────────────────────────────────────

/** Date mode for the date source type */
export type DateColumnMode = 'today' | 'custom' | 'range_end';

/** Date column config stored in transform_config */
export interface DateColumnConfig {
  dateMode: DateColumnMode;
  dateFormat: string;
  /** Only used when dateMode is 'custom' — stored as YYYY-MM-DD */
  customDate?: string;
}

/** Date mode display info */
export const DATE_MODE_INFO: Record<DateColumnMode, { label: string; description: string }> = {
  today: {
    label: "Today's Date",
    description: 'Automatically inserts the current date at the time of export',
  },
  custom: {
    label: 'Custom Date',
    description: 'Use a specific fixed date for every export',
  },
  range_end: {
    label: 'Last Day of Export',
    description: "Automatically inserts the export's Date To filter value",
  },
};


export interface SourceFieldOption {
  field: string;
  label: string;
  group: string;
  description?: string;
}

/** All available source fields grouped by origin */
export const SOURCE_FIELD_OPTIONS: SourceFieldOption[] = [
  // TaxiCaller
  { field: 'job_id', label: 'Job ID', group: 'TaxiCaller' },
  { field: 'project', label: 'Project', group: 'TaxiCaller' },
  { field: 'account_id', label: 'Account ID', group: 'TaxiCaller' },
  { field: 'reference', label: 'Reference', group: 'TaxiCaller' },
  { field: 'passenger_first_name', label: 'Passenger First Name', group: 'TaxiCaller' },
  { field: 'passenger_last_name', label: 'Passenger Last Name', group: 'TaxiCaller' },
  { field: 'passenger_name', label: 'Passenger Name (Combined)', group: 'TaxiCaller' },
  { field: 'pick_up_location', label: 'Pickup Location', group: 'TaxiCaller' },
  { field: 'drop_off_location', label: 'Dropoff Location', group: 'TaxiCaller' },
  { field: 'pu_date', label: 'Pickup Date', group: 'TaxiCaller' },
  { field: 'pu_time', label: 'Pickup Time', group: 'TaxiCaller' },
  { field: 'do_time', label: 'Dropoff Time', group: 'TaxiCaller' },
  { field: 'tc_vehicle_number', label: 'Vehicle Number', group: 'TaxiCaller' },
  { field: 'tc_driver_name', label: 'Driver Name', group: 'TaxiCaller' },
  { field: 'grand_total', label: 'Grand Total', group: 'TaxiCaller' },
  { field: 'direct_settled', label: 'Direct Settled', group: 'TaxiCaller' },
  { field: 'tc_cost_code', label: 'Cost Code', group: 'TaxiCaller' },
  { field: 'stage', label: 'Stage', group: 'TaxiCaller' },
  { field: 'fleet', label: 'Fleet', group: 'TaxiCaller' },
  { field: 'trip_total', label: 'Trip Total', group: 'TaxiCaller' },
  { field: 'trip_base_fare', label: 'Trip Base Fare', group: 'TaxiCaller' },
  { field: 'job_km', label: 'Job KM', group: 'TaxiCaller' },

  // Driver Completed Payments
  { field: 'paid_amount', label: 'Paid Amount', group: 'Driver Completed Payments' },
  { field: 'cash_amt', label: 'Cash Amount', group: 'Driver Completed Payments' },
  { field: 'eftpos_amt', label: 'EFTPOS Amount', group: 'Driver Completed Payments' },
  { field: 'totalmobility_amt', label: 'Total Mobility Amount', group: 'Driver Completed Payments' },
  { field: 'tm_hoist_amt', label: 'TM Hoist Amount', group: 'Driver Completed Payments' },
  { field: 'account_amt', label: 'Account Amount', group: 'Driver Completed Payments' },
  { field: 'other_amt', label: 'Other Amount', group: 'Driver Completed Payments' },
  { field: 'discount', label: 'Discount', group: 'Driver Completed Payments' },
  { field: 'ecard_amt', label: 'eCard Amount', group: 'Driver Completed Payments' },
  { field: 'payment_notes', label: 'Payment Notes', group: 'Driver Completed Payments' },

  // CRM Accounts
  { field: 'account_name', label: 'Account Name', group: 'CRM Accounts' },
  { field: 'account_type', label: 'Account Type', group: 'CRM Accounts' },
  { field: 'account_series_type', label: 'Account Series Type', group: 'CRM Accounts' },
  { field: 'billed_status', label: 'Billed Status', group: 'CRM Accounts' },
  { field: 'billing_name', label: 'Billing Name', group: 'CRM Accounts' },
  { field: 'invoice_email', label: 'Invoice Email', group: 'CRM Accounts' },
  { field: 'accounts_payable_email', label: 'Accounts Payable Email', group: 'CRM Accounts' },
  { field: 'payment_terms', label: 'Payment Terms', group: 'CRM Accounts' },
  { field: 'account_category', label: 'Account Category', group: 'CRM Accounts' },
  { field: 'crm_default_project', label: 'CRM Default Project', group: 'CRM Accounts' },
  { field: 'crm_default_cost_code', label: 'CRM Default Cost Code', group: 'CRM Accounts' },
  { field: 'crm_default_reference', label: 'CRM Default Reference', group: 'CRM Accounts' },

  // Derived Fields
  { field: 'account_series', label: 'Account Series', group: 'Derived Fields', description: 'Derived from account_id' },
  { field: 'net_after_tm', label: 'Net After TM', group: 'Derived Fields', description: 'grand_total - totalmobility_amt' },

  // Invoice Derived Fields (Part 6)
  { field: 'invoice_passenger_name', label: 'Invoice Passenger Name', group: 'Invoice Fields', description: 'Best available passenger identity for grouping' },
  { field: 'invoice_group_key', label: 'Invoice Group Key', group: 'Invoice Fields', description: 'Grouping key used for invoice assignment' },
  { field: 'assigned_invoice_number', label: 'Assigned Invoice Number', group: 'Invoice Fields', description: 'Sequential invoice number from the current run' },
];


/** Prefix used to identify references to other calculated template columns */
export const CALC_COL_PREFIX = '__calc_col__::';

/** Helper to create a calculated column reference field identifier */
export const makeCalcColField = (columnId: string) => `${CALC_COL_PREFIX}${columnId}`;

/** Helper to check if a field identifier is a calculated column reference */
export const isCalcColField = (field: string) => field.startsWith(CALC_COL_PREFIX);

/** Helper to extract the column ID from a calculated column reference */
export const getCalcColId = (field: string) => field.replace(CALC_COL_PREFIX, '');

/** Prefix used to identify margin rule factor references in field pickers */
export const MARGIN_RULE_PREFIX = '__margin_rule__::';

/** Helper to create a margin rule factor field identifier */
export const makeMarginField = (ruleId: string) => `${MARGIN_RULE_PREFIX}${ruleId}`;

/** Helper to check if a field identifier is a margin rule factor reference */
export const isMarginField = (field: string) => field.startsWith(MARGIN_RULE_PREFIX);

/** Helper to extract the margin rule ID from a margin rule factor reference */
export const getMarginRuleId = (field: string) => field.replace(MARGIN_RULE_PREFIX, '');

/** Grouped source fields for the picker */
export const SOURCE_FIELD_GROUPS = ['TaxiCaller', 'Driver Completed Payments', 'CRM Accounts', 'Derived Fields', 'Invoice Fields'] as const;





// ─── Margin Rule Types (Part 5) ─────────────────────────────────────

/** Margin type for pricing adjustments */
export type MarginType = 'percentage' | 'fixed_amount';

/** Rounding mode after margin is applied */
export type RoundingMode = 'none' | 'nearest' | 'up' | 'down';

/** Target scope for which rows a margin rule applies to */
export type TargetScope = 'all_rows' | 'account_series' | 'account_id' | 'account_name' | 'project' | 'cost_code';

/** Margin rule record from the database */
export interface MarginRule {
  id: string;
  template_id: string;
  rule_name: string;
  is_active: boolean;
  applies_to_field: string;
  margin_type: MarginType;
  margin_value: number;
  rounding_mode: RoundingMode;
  rounding_unit: number;
  target_scope: TargetScope;
  target_config: Record<string, any>;
  sort_order: number;
  notes: string | null;
  created_at: string;
  updated_at: string;
  created_by: string | null;
  updated_by: string | null;
}

/** Margin rule with source template info — used when combining rules from multiple templates */
export interface MarginRuleWithSource extends MarginRule {
  /** The name of the template this rule belongs to */
  _templateName: string;
  /** Whether this rule belongs to the currently selected template */
  _isCurrentTemplate: boolean;
}

export interface OtherTemplateRulesGroup {
  templateId: string;
  templateName: string;
  rules: MarginRule[];
}



/** Result of applying a margin rule to a value */
export interface MarginResult {
  baseValue: number;
  adjustedPreRound: number;
  finalValue: number;
  appliedRule: MarginRule | null;
  wasAdjusted: boolean;
}

/** Margin type display info */
export const MARGIN_TYPE_INFO: Record<MarginType, { label: string; symbol: string; description: string }> = {
  percentage: {
    label: 'Percentage',
    symbol: '%',
    description: 'Apply a percentage uplift or reduction',
  },
  fixed_amount: {
    label: 'Fixed Amount',
    symbol: '$',
    description: 'Add or subtract a fixed dollar amount',
  },
};

/** Rounding mode display info */
export const ROUNDING_MODE_INFO: Record<RoundingMode, { label: string; description: string }> = {
  none: { label: 'No Rounding', description: 'Keep exact value after margin' },
  nearest: { label: 'Nearest', description: 'Round to nearest unit' },
  up: { label: 'Round Up', description: 'Always round up to next unit' },
  down: { label: 'Round Down', description: 'Always round down to previous unit' },
};

/** Common rounding unit presets */
export const ROUNDING_UNIT_PRESETS = [
  { value: 0.01, label: '$0.01 (cent)' },
  { value: 0.05, label: '$0.05' },
  { value: 0.10, label: '$0.10' },
  { value: 0.50, label: '$0.50' },
  { value: 1, label: '$1' },
  { value: 5, label: '$5' },
  { value: 10, label: '$10' },
  { value: 20, label: '$20' },
  { value: 50, label: '$50' },
] as const;

/** Target scope display info */
export const TARGET_SCOPE_INFO: Record<TargetScope, { label: string; description: string; needsValue: boolean }> = {
  all_rows: { label: 'All Rows', description: 'Apply to every row in the template', needsValue: false },
  account_series: { label: 'Account Series', description: 'Only rows matching a specific account series (e.g. 6)', needsValue: true },
  account_id: { label: 'Account ID', description: 'Only rows matching a specific account number', needsValue: true },
  account_name: { label: 'Account Name', description: 'Only rows matching an account name (contains)', needsValue: true },
  project: { label: 'Project', description: 'Only rows matching a specific project code', needsValue: true },
  cost_code: { label: 'Cost Code', description: 'Only rows matching a specific cost code', needsValue: true },
};

/** Numeric source fields that margin rules can target */
export const NUMERIC_SOURCE_FIELDS = [
  { field: 'grand_total', label: 'Grand Total' },
  { field: 'trip_total', label: 'Trip Total' },
  { field: 'trip_base_fare', label: 'Trip Base Fare' },
  { field: 'direct_settled', label: 'Direct Settled' },
  { field: 'paid_amount', label: 'Paid Amount' },
  { field: 'cash_amt', label: 'Cash Amount' },
  { field: 'eftpos_amt', label: 'EFTPOS Amount' },
  { field: 'totalmobility_amt', label: 'Total Mobility Amount' },
  { field: 'tm_hoist_amt', label: 'TM Hoist Amount' },
  { field: 'account_amt', label: 'Account Amount' },
  { field: 'other_amt', label: 'Other Amount' },
  { field: 'discount', label: 'Discount' },
  { field: 'ecard_amt', label: 'eCard Amount' },
  { field: 'net_after_tm', label: 'Net After TM' },
  { field: 'job_km', label: 'Job KM' },
] as const;

// ─── Helpers ────────────────────────────────────────────────────────

/** Helper to get current month date range */
export function getCurrentMonthRange(): { from: string; to: string } {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const from = `${year}-${String(month + 1).padStart(2, '0')}-01`;
  const lastDay = new Date(year, month + 1, 0).getDate();
  const to = `${year}-${String(month + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
  return { from, to };
}

/** Derive validation status from a row */
export function getValidationStatus(row: ExportJobRow): ValidationStatus {
  if (!row.dcp_id) return 'no_payment';

  const checks: boolean[] = [];
  if (Number(row.eftpos_amt || 0) > 0) checks.push(!!row.eftpos_valid);
  if (Number(row.totalmobility_amt || 0) > 0) checks.push(!!row.total_mobility_valid);
  if (Number(row.tm_hoist_amt || 0) > 0) checks.push(!!row.tm_hoist_valid);
  checks.push(!!row.reconciled);

  if (checks.length === 0) return 'not_validated';
  if (checks.every(Boolean)) return 'fully_validated';
  if (checks.some(Boolean)) return 'partially_validated';
  return 'not_validated';
}

/** Compute summary stats from rows */
export function computeSummaryStats(rows: ExportJobRow[]): ExportSummaryStats {
  const stats: ExportSummaryStats = {
    rowCount: rows.length,
    totalGrandTotal: 0,
    totalTM: 0,
    totalHoist: 0,
    totalNetAfterTM: 0,
    totalAccountAmt: 0,
    totalCash: 0,
    totalEftpos: 0,
    fullyValidated: 0,
    partiallyValidated: 0,
    notValidated: 0,
    noPayment: 0,
  };

  for (const row of rows) {
    stats.totalGrandTotal += Number(row.grand_total || 0);
    stats.totalTM += Number(row.totalmobility_amt || 0);
    stats.totalHoist += Number(row.tm_hoist_amt || 0);
    stats.totalNetAfterTM += Number(row.net_after_tm || 0);
    stats.totalAccountAmt += Number(row.account_amt || 0);
    stats.totalCash += Number(row.cash_amt || 0);
    stats.totalEftpos += Number(row.eftpos_amt || 0);

    const vs = getValidationStatus(row);
    if (vs === 'fully_validated') stats.fullyValidated++;
    else if (vs === 'partially_validated') stats.partiallyValidated++;
    else if (vs === 'not_validated') stats.notValidated++;
    else stats.noPayment++;
  }

  return stats;
}


// ═══════════════════════════════════════════════════════════════════════
// Part 6: Invoice Run Types
// ═══════════════════════════════════════════════════════════════════════

/** Invoice grouping mode determines how rows are grouped into invoices */
export type InvoiceGroupingMode =
  | 'account_only'
  | 'account_plus_passenger'
  | 'account_plus_reference'
  | 'custom_fields';

/** Invoice run settings configured by the admin */
export interface InvoiceRunSettings {
  startingInvoiceNumber: number;
  groupingMode: InvoiceGroupingMode;
  /** For custom_fields mode: ordered list of field names to group by */
  customGroupFields: string[];
}

/** Per-row invoice assignment result */
export interface InvoiceAssignment {
  /** The row identifier (tc_id) */
  rowId: string;
  /** Derived passenger name for this row */
  invoicePassengerName: string;
  /** The grouping key that determined which invoice this row belongs to */
  invoiceGroupKey: string;
  /** The assigned sequential invoice number */
  assignedInvoiceNumber: number;
}

/** Summary of an invoice run */
export interface InvoiceRunSummary {
  startingNumber: number;
  endingNumber: number;
  uniqueGroups: number;
  totalRows: number;
  /** Breakdown per invoice group */
  groupBreakdown: {
    invoiceNumber: number;
    groupKey: string;
    rowCount: number;
    accountId: string | null;
    passengerName: string | null;
  }[];
}

/** Invoice grouping mode display info */
export const INVOICE_GROUPING_MODE_INFO: Record<InvoiceGroupingMode, {
  label: string;
  description: string;
  icon: string;
}> = {
  account_only: {
    label: 'Account Only',
    description: 'All rows with the same account share one invoice number',
    icon: 'Building2',
  },
  account_plus_passenger: {
    label: 'Account + Passenger',
    description: 'Same account + same passenger share one invoice. Different passengers get separate invoices.',
    icon: 'Users',
  },
  account_plus_reference: {
    label: 'Account + Reference',
    description: 'Same account + same reference share one invoice number',
    icon: 'Tag',
  },
  custom_fields: {
    label: 'Custom Fields',
    description: 'Choose 1-4 fields that together define the invoice grouping',
    icon: 'Settings2',
  },
};

/** Fields available for custom invoice grouping */
export const INVOICE_GROUPING_FIELDS = [
  { field: 'account_id', label: 'Account ID' },
  { field: 'account_name', label: 'Account Name' },
  { field: 'billing_name', label: 'Billing Name' },
  { field: 'passenger_name', label: 'Passenger Name' },
  { field: 'reference', label: 'Reference' },
  { field: 'tc_cost_code', label: 'Cost Code' },
  { field: 'project', label: 'Project' },
  { field: 'pu_date', label: 'Pickup Date' },
  { field: 'account_series', label: 'Account Series' },
] as const;



// ═══════════════════════════════════════════════════════════════════════
// Part 7: Anomaly Detection Types
// ═══════════════════════════════════════════════════════════════════════

/** Anomaly types supported by the detection engine */
export type AnomalyType =
  | 'fare_variance'
  | 'missing_return'
  | 'missing_fields'
  | 'validation_issue'
  | 'tm_mismatch'
  | 'duplicate_trip'
  | 'outlier_pricing';

/** Review status for a flagged anomaly */
export type AnomalyReviewStatus = 'flagged' | 'reviewed' | 'ignored';

/** Return trip time window for matching */
export type ReturnTripWindow = 'same_day' | '24h' | '48h';

/** Configuration for the anomaly detection engine */
export interface AnomalyConfig {
  /** Fare variance threshold in dollars (default $5) */
  fareVarianceThreshold: number;
  /** Return trip matching window */
  returnTripWindow: ReturnTripWindow;
  /** Which anomaly types are enabled */
  enabledTypes: Set<AnomalyType>;
  /** Outlier pricing: number of standard deviations to flag */
  outlierStdDevMultiplier: number;
}

/** Default anomaly config */
export const DEFAULT_ANOMALY_CONFIG: AnomalyConfig = {
  fareVarianceThreshold: 5,
  returnTripWindow: '24h',
  enabledTypes: new Set<AnomalyType>([
    'fare_variance',
    'missing_return',
    'missing_fields',
    'validation_issue',
    'tm_mismatch',
    'duplicate_trip',
    'outlier_pricing',
  ]),
  outlierStdDevMultiplier: 2,
};

/** A single anomaly flag on a row */
export interface AnomalyFlag {
  /** Unique ID for this flag instance */
  id: string;
  /** The anomaly type */
  type: AnomalyType;
  /** Severity: warning or error */
  severity: 'warning' | 'error';
  /** Human-readable description of the anomaly */
  message: string;
  /** Optional detail (e.g. the related job_id for return trip) */
  detail?: string;
  /** Optional numeric value (e.g. fare difference) */
  numericDetail?: number;
  /** Related row tc_id (e.g. the matching return trip) */
  relatedRowId?: string;
}

/** Per-row anomaly result */
export interface RowAnomalyResult {
  /** The row's tc_id */
  rowId: string;
  /** The row's job_id for display */
  jobId: string;
  /** All anomaly flags for this row */
  flags: AnomalyFlag[];
  /** Review status from database (if persisted) */
  reviewStatus: AnomalyReviewStatus;
  /** Review notes from database */
  reviewNotes: string | null;
  /** Reviewer identity */
  reviewedBy: string | null;
  /** When reviewed */
  reviewedAt: string | null;
}

/** Summary of anomaly detection results */
export interface AnomalySummary {
  totalFlagged: number;
  totalFlags: number;
  byType: Record<AnomalyType, number>;
  reviewed: number;
  ignored: number;
  unreviewed: number;
}

/** Database record for export_anomaly_reviews */
export interface AnomalyReviewRecord {
  id: string;
  job_id: string;
  anomaly_type: string;
  status: AnomalyReviewStatus;
  notes: string | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  created_at: string;
}

/** Anomaly type display info */
export const ANOMALY_TYPE_INFO: Record<AnomalyType, {
  label: string;
  shortLabel: string;
  description: string;
  color: string;
  badgeColor: string;
  severity: 'warning' | 'error';
}> = {
  fare_variance: {
    label: 'Fare Variance',
    shortLabel: 'Fare',
    description: 'Outbound/return fare difference exceeds threshold',
    color: 'text-amber-400',
    badgeColor: 'bg-amber-900/30 text-amber-400 border-amber-700/40',
    severity: 'warning',
  },
  missing_return: {
    label: 'Missing Return Trip',
    shortLabel: 'Return',
    description: 'No corresponding return trip found within time window',
    color: 'text-orange-400',
    badgeColor: 'bg-orange-900/30 text-orange-400 border-orange-700/40',
    severity: 'warning',
  },
  missing_fields: {
    label: 'Missing Required Fields',
    shortLabel: 'Fields',
    description: 'Required fields are missing (account, project, cost code, reference)',
    color: 'text-red-400',
    badgeColor: 'bg-red-900/30 text-red-400 border-red-700/40',
    severity: 'error',
  },
  validation_issue: {
    label: 'Validation Issue',
    shortLabel: 'Valid',
    description: 'Payment validation flags are not passing',
    color: 'text-rose-400',
    badgeColor: 'bg-rose-900/30 text-rose-400 border-rose-700/40',
    severity: 'error',
  },
  tm_mismatch: {
    label: 'TM Mismatch',
    shortLabel: 'TM',
    description: 'Total Mobility amount exists but may not be deducted properly',
    color: 'text-purple-400',
    badgeColor: 'bg-purple-900/30 text-purple-400 border-purple-700/40',
    severity: 'warning',
  },
  duplicate_trip: {
    label: 'Duplicate / Near-Duplicate',
    shortLabel: 'Dupe',
    description: 'Very similar trip found (same passenger, similar route/time)',
    color: 'text-blue-400',
    badgeColor: 'bg-blue-900/30 text-blue-400 border-blue-700/40',
    severity: 'warning',
  },
  outlier_pricing: {
    label: 'Outlier Pricing',
    shortLabel: 'Outlier',
    description: 'Fare significantly outside typical range for similar trips',
    color: 'text-cyan-400',
    badgeColor: 'bg-cyan-900/30 text-cyan-400 border-cyan-700/40',
    severity: 'warning',
  },
};

/** Return trip window display info */
export const RETURN_TRIP_WINDOW_INFO: Record<ReturnTripWindow, { label: string; hours: number }> = {
  same_day: { label: 'Same Day', hours: 0 },
  '24h': { label: 'Within 24 Hours', hours: 24 },
  '48h': { label: 'Within 48 Hours', hours: 48 },
};



// ═══════════════════════════════════════════════════════════════════════
// Part 9: Export Run History Types
// ═══════════════════════════════════════════════════════════════════════

/** Export run record from the database */
export interface ExportRun {
  id: string;
  template_id: string | null;
  template_name_snapshot: string;
  target_system_snapshot: string | null;
  file_type: string;
  file_name: string;
  sheet_name: string | null;
  export_scope: string;
  include_headers: boolean;
  starting_invoice_number: number | null;
  ending_invoice_number: number | null;
  invoice_group_count: number | null;
  invoice_grouping_mode: string | null;
  invoice_grouping_config: any | null;
  row_count: number;
  column_count: number;
  flagged_row_count: number;
  reviewed_row_count: number;
  ignored_row_count: number;
  excluded_row_count: number;
  margin_rule_count: number;
  grand_total_snapshot: number | null;
  filters_snapshot: any | null;
  template_columns_snapshot: any | null;
  margin_rules_snapshot: any | null;
  invoice_settings_snapshot: any | null;
  anomaly_config_snapshot: any | null;
  notes: string | null;
  exported_by: string;
  exported_at: string;
  created_at: string;
  // Reversal tracking
  status: 'active' | 'reversed';
  reversed_at: string | null;
  reversed_by: string | null;
  reversal_reason: string | null;
}


/** Input for creating an export run record */
export interface ExportRunInput {
  template_id: string | null;
  template_name_snapshot: string;
  target_system_snapshot: string | null;
  file_type: string;
  file_name: string;
  sheet_name: string | null;
  export_scope: string;
  include_headers: boolean;
  starting_invoice_number: number | null;
  ending_invoice_number: number | null;
  invoice_group_count: number | null;
  invoice_grouping_mode: string | null;
  invoice_grouping_config: any | null;
  row_count: number;
  column_count: number;
  flagged_row_count: number;
  reviewed_row_count: number;
  ignored_row_count: number;
  excluded_row_count: number;
  margin_rule_count: number;
  grand_total_snapshot: number | null;
  filters_snapshot: any | null;
  template_columns_snapshot: any | null;
  margin_rules_snapshot: any | null;
  invoice_settings_snapshot: any | null;
  anomaly_config_snapshot: any | null;
  notes: string | null;
  exported_by: string;
}
